import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearch } from "../contexts/SearchContext";
import {
  filterGlobalCategorySearch,
  filterProductSearch,
  getTabLink,
  parameterToTabType,
} from "../extensions/helpers";
import { Tab } from "../types/Tab";
import ProductList from "./ProductList";
import SearchButton from "./SearchButton";
import { useNavigate } from "react-router-dom";
import MenuCloseButton from "./MenuCloseButton";
import { CatalogObject } from "square";
import { useCatalog } from "../contexts/CatalogContext";
import { findOrGetImages } from "../api";
import { useShopDomainName, useShopLink } from "../hooks/useShopLink";

const SearchMenu: React.FC = () => {
  const {
    items,
    categories,
    getCategoryById,
    getCategoryByName,
    isTheme,
    addImages,
    getImagesByIds,
  } = useCatalog();
  const navigate = useNavigate();

  // Both resolve against the live catalog, so a category rename in Square
  // moves these CTAs instead of breaking them. See hooks/useShopLink.ts.
  const shopDomainName = useShopDomainName();
  const shopLink = useShopLink();
  const sweatersLink = useShopLink("Sweaters");

  const { isSearchMenuOpen, setIsSearchMenuOpen } = useSearch();
  const [searchInput, setSearchInput] = useState<string>("");

  const [domainSearchFilter, setDomainSearchFilter] = useState<string[]>([]);
  const [categorySearchFilter, setCategorySearchFilter] = useState<string[]>(
    [],
  );
  const [topProducts, setTopProducts] = useState<CatalogObject[]>(
    items?.slice(0, 4),
  );

  const searchableCategories = categories?.filter(
    (c) => c.categoryData?.parentCategory != null,
  );
  const searchableThemes = categories?.filter((c) => isTheme(c.id));

  // The debounced callback is created once, so anything it closes over is
  // frozen at first render — when the catalog is still empty. Read the live
  // values through a ref instead. (This is what broke product search: the
  // debounced fn called a handler that read `searchInput` from the first
  // render, i.e. "", so every lookup requested `?searchQuery=` and 400'd.)
  const latest = useRef({ items, searchableThemes, searchableCategories });
  latest.current = { items, searchableThemes, searchableCategories };

  // Search runs entirely against the catalog the browser already has. The
  // storefront downloads the full catalog into CatalogContext, so there is no
  // reason to round-trip to Square on every keystroke — that was both slow and
  // wasteful, and it bypassed the visibility filtering `get-catalog` applies.
  const runSearch = useCallback((query: string) => {
    const {
      items: liveItems,
      searchableThemes: themes,
      searchableCategories: cats,
    } = latest.current;

    const trimmed = query?.trim() ?? "";
    if (!trimmed) {
      setDomainSearchFilter([]);
      setCategorySearchFilter([]);
      setTopProducts(liveItems?.slice(0, 4) ?? []);
      return;
    }

    setDomainSearchFilter(
      filterGlobalCategorySearch(
        trimmed,
        themes?.map((t) => t.categoryData?.name ?? "") ?? [],
      ),
    );
    setCategorySearchFilter(
      filterGlobalCategorySearch(
        trimmed,
        cats?.map((c) => c.categoryData?.name ?? "") ?? [],
      ),
    );
    setTopProducts(filterProductSearch(trimmed, liveItems ?? []));
  }, []);

  // Re-run the match whenever the query OR the catalog changes. Keying on
  // `items` is what covers the first-load race: a visitor who opens search and
  // types before the catalog has downloaded would otherwise be stuck on "No
  // results found" until the next keystroke, because runSearch read an empty
  // `items` through the ref. This re-fires the same query once the catalog
  // arrives. (With no query, runSearch resets to the first few items.)
  useEffect(() => {
    runSearch(searchInput);
  }, [items, searchInput, runSearch]);

  // Matching is local, but IMAGES still aren't: CatalogProvider loads ITEMs
  // only, and the image cache fills lazily as a visitor browses. Nothing fills
  // it for a visitor who lands on a content page and opens search, so fetch
  // the thumbnails for whatever we're about to show. findOrGetImages already
  // skips ids that are cached, so this is a no-op once they are.
  useEffect(() => {
    if (topProducts.length === 0) return;
    findOrGetImages(topProducts, addImages, getImagesByIds).catch((error) => {
      console.error("Get search result images failed:", error);
    });
    // getImagesByIds/addImages are re-created on every catalog render; keying
    // on them would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topProducts]);

  // The search effect (keyed on searchInput) runs the match — setting the
  // query is enough, no debounce needed when there's no network call.
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const clearSearch = () => {
    setSearchInput("");
  };

  const goTo = (link: string) => {
    setIsSearchMenuOpen(false);
    navigate(link, {
      replace: true,
    });
    setSearchInput("");
    // window.location.reload();
  };

  const handleClickCard = (tab?: Tab, domain?: string, category?: string) => {
    goTo(
      getTabLink({
        tab,
        domain,
        category,
      }),
    );
  };

  return (
    <div
      className={`search-menu ${
        isSearchMenuOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="drawer-header">
        <h2 className="drawer-title">Search</h2>
        <MenuCloseButton
          onClick={() => setIsSearchMenuOpen(!isSearchMenuOpen)}
        />
      </div>

      <div className="shrink-0 px-5 pt-4 pb-3">
        <div className="search-bar">
          {/* SVG Magnifying Glass Icon */}
          <SearchButton className="search-menu-search-button" />
          <input
            type="text"
            value={searchInput}
            onChange={handleSearchChange}
            className="search-input"
            placeholder="Search products..."
            autoFocus
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              className="clear-button"
              aria-label="Clear search"
            >
              &times; {/* This is the "X" symbol */}
            </button>
          )}
        </div>
      </div>

      <div className="drawer-body px-5 pb-6 flex flex-col gap-5">
        {searchInput != null && searchInput !== "" ? (
          // "No results" must account for matching PRODUCTS too, not just
          // categories/themes — product hits render in Top Products below, so
          // searching an item by name used to show "No Results Found" with the
          // matching product sitting right underneath it.
          domainSearchFilter.length <= 0 &&
          categorySearchFilter.length <= 0 &&
          topProducts.length <= 0 ? (
            // No loading state needed — the match is synchronous and local.
            <div className="flex flex-col items-center gap-1 py-12 text-center">
              <h2>No results found</h2>
              {/* The way out has to be clickable — this dead end was the one
                  place in the drawer with no control on it. */}
              <p className="text-sm text-gray-500 lora-text">
                Try a different word, or{" "}
                <button
                  type="button"
                  onClick={() => goTo(shopLink)}
                  className="font-semibold text-brown-dark underline underline-offset-2 hover:text-saddle"
                >
                  browse {shopDomainName}
                </button>
                .
              </p>
            </div>
          ) : (
            <>
              {domainSearchFilter.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h2>Categories</h2>
                  <div className="flex flex-wrap gap-2">
                    {domainSearchFilter.map((c, i) => (
                      <SearchButton
                        key={i}
                        label={c}
                        hideIcon
                        onClick={() => handleClickCard(parameterToTabType(c))}
                        className="search-chip"
                      />
                    ))}
                  </div>
                </div>
              )}

              {categorySearchFilter.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h2>Product Lines</h2>
                  <div className="flex flex-wrap gap-2">
                    {categorySearchFilter.map((c, i) => (
                      <SearchButton
                        key={i}
                        label={c}
                        hideIcon
                        onClick={() =>
                          handleClickCard(
                            undefined,
                            getCategoryById(
                              getCategoryByName(c)?.categoryData?.parentCategory
                                ?.id ?? "",
                            )?.categoryData?.name ?? undefined,
                            c,
                          )
                        }
                        className="search-chip"
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )
        ) : (
          <div className="flex flex-col gap-2">
            <h2>Popular Lately</h2>
            <div className="flex flex-wrap gap-2">
              <SearchButton
                label="Sweaters"
                hideIcon
                onClick={() => goTo(sweatersLink)}
                className="search-chip"
              />
            </div>
          </div>
        )}

        {topProducts.length > 0 && (
          <div className="flex flex-col gap-2">
            <h2>{searchInput ? "Products" : "Top Products"}</h2>
            <ProductList items={topProducts} isSmallVersion forceReload />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchMenu;
